<template>
  <div
    v-if="$can('read', 'Coupon')"
    style="height: inherit"
  >

    <!-- Filters -->
    <refund-list-filters
      :brand-filter.sync="brandFilter"
      :merchant-filter.sync="merchantFilter"
      :code-filter.sync="codeFilter"
      :refid-filter.sync="refidFilter"
      :brand-options="brandOptions"
      :merchant-options="merchantOptions"
    />

    <!-- Table Container Card -->
    <b-card
      no-body
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-form-checkbox
              v-model="checkSelected"
              class="custom-control-primary"
              switch
              @change="changeSelect"
            >
              <span class="switch-icon-left">
                <feather-icon icon="CheckIcon" />
              </span>
              <span class="switch-icon-right">
                <feather-icon icon="XIcon" />
              </span>
            </b-form-checkbox>
            <label>{{ $t('Entries') }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            />
          </b-col>

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <b-form-input
                v-model="searchQuery"
                class="d-inline-block mr-1"
                :placeholder="`${$t('Search')}...`"
              />
            </div>
          </b-col>
        </b-row>

      </div>

      <b-table
        ref="refRefundListTable"
        :items="getRefunds"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        hover
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        class="position-relative"
        selectable
        select-mode="multi"
        @row-selected="onRowSelected"
      >

        <!-- Column: Date/Time,Note,By -->
        <template #cell(date_time)="data">
          <div>
            <b-badge
              pill
              :variant="(skin === 'dark')? `light-warning` : `warning`"
            >
              {{ data.item.refund_date }}
            </b-badge>
            <b-badge
              pill
              :variant="(skin === 'dark')? `light-warning` : `warning`"
              style="margin-left: 0.4em;"
            >
              {{ data.item.refund_time }}
            </b-badge>
          </div>

          <div
            style="margin-top: 0.6rem;"
          >
            <b-list-group>
              <b-list-group-item style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;">
                <small>{{ (data.item.note !== '')? data.item.note : '-' }}</small>
              </b-list-group-item>
            </b-list-group>
            <b-list-group
              style="margin-top: 0.2rem;"
              horizontal="md"
            >
              <b-list-group-item
                style="padding: 0.2rem 0.6rem 0.2rem 0.6rem; width: 3rem;"
              >
                <small>{{ `${$t('By')}` }}</small>
              </b-list-group-item>
              <b-list-group-item style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;">
                <small>{{ (data.item.redeem_username !== '')? data.item.redeem_username : '-' }}</small>
              </b-list-group-item>
            </b-list-group>
            <b-list-group
              style="margin-top: 0.2rem;"
              horizontal="md"
            >
              <b-list-group-item
                style="padding: 0.2rem 0.6rem 0.2rem 0.6rem; width: 3rem;"
              >
                <small>{{ `${$t('ID')}` }}</small>
              </b-list-group-item>
              <b-list-group-item style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;">
                <small>#{{ (data.item.id !== '')? data.item.id : '-' }}</small>
              </b-list-group-item>
            </b-list-group>
          </div>
        </template>

        <!-- Column: Brand,Merchant,Coupon -->
        <template #cell(coupon)="data">
          <div>
            <b-list-group>
              <b-list-group-item
                style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
              >
                <span class="align-text-top">{{ data.item.brand_name }}</span>
              </b-list-group-item>
              <b-list-group-item
                style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;"
              >
                <span class="align-text-top">{{ data.item.merchant_name }}</span>
              </b-list-group-item>
            </b-list-group>
          </div>
          <div style="margin-top: 0.6em;">
            <b-badge
              pill
              :variant="(skin === 'dark')? `light-info` : `info`"
            >
              {{ data.item.code }}
            </b-badge>
          </div>
          <div style="margin-top: 0.6em;">
            <b-badge
              pill
              :variant="(skin === 'dark')? `light-success` : `success`"
            >
              ฿ {{ data.item.value }}
            </b-badge>
          </div>
        </template>

        <!-- Column: Redeem/Date Time/Username -->
        <template #cell(redeem_datetime_username)="data">
          <div>
            <b-badge
              pill
              :variant="(skin === 'dark')? `light-danger` : `danger`"
            >
              {{ data.item.redeem.date }}
            </b-badge>
            <b-badge
              pill
              :variant="(skin === 'dark')? `light-danger` : `danger`"
              style="margin-left: 0.4em;"
            >
              {{ data.item.redeem.time }}
            </b-badge>
          </div>
          <div
            v-if="!!data.item.redeem"
            style="margin-top: 0.6rem;"
          >
            <b-list-group horizontal="md">
              <b-list-group-item
                style="padding: 0.2rem 0.6rem 0.2rem 0.6rem; width: 3rem;"
              >
                <small>{{ `${$t('By')}` }}</small>
              </b-list-group-item>
              <b-list-group-item style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;">
                <small>{{ (data.item.redeem.username !== '')? data.item.redeem.username : '-' }}</small>
              </b-list-group-item>
            </b-list-group>
            <b-list-group
              style="margin-top: 0.2rem;"
              horizontal="md"
            >
              <b-list-group-item
                style="padding: 0.2rem 0.6rem 0.2rem 0.6rem; width: 3rem;"
              >
                <small>{{ `${$t('ID')}` }}</small>
              </b-list-group-item>
              <b-list-group-item style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;">
                <small>#{{ (data.item.redeem_id !== '')? data.item.redeem_id : '-' }}</small>
              </b-list-group-item>
            </b-list-group>
          </div>
        </template>

        <!-- Column: RefID/Reference/Point -->
        <template #cell(refid_reference_point)="data">
          <div>
            <b-list-group horizontal="md">
              <b-list-group-item
                style="padding: 0.2rem 0.6rem 0.2rem 0.6rem; width: 6rem;"
              >
                <small>{{ `${$t('Ref ID')}` }}</small>
              </b-list-group-item>
              <b-list-group-item style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;">
                <b-badge
                  pill
                  :variant="(skin === 'dark')? `light-danger` : `danger`"
                >
                  {{ (data.item.ref_id !== '')? data.item.ref_id : '' }}
                </b-badge>
              </b-list-group-item>
            </b-list-group>
            <b-list-group
              style="margin-top: 0.2rem;"
              horizontal="md"
            >
              <b-list-group-item
                style="padding: 0.2rem 0.6rem 0.2rem 0.6rem; width: 6rem;"
              >
                <small>{{ `${$t('Reference')}` }}</small>
              </b-list-group-item>
              <b-list-group-item style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;">
                <b-badge
                  pill
                  :variant="(skin === 'dark')? `light-danger` : `danger`"
                >
                  {{ (data.item.reference !== '')? data.item.reference : '' }}
                </b-badge>
              </b-list-group-item>
            </b-list-group>
            <b-list-group
              style="margin-top: 0.2rem;"
              horizontal="md"
            >
              <b-list-group-item
                style="padding: 0.2rem 0.6rem 0.2rem 0.6rem; width: 6rem;"
              >
                <small>{{ `${$t('Point')}` }}</small>
              </b-list-group-item>
              <b-list-group-item style="padding: 0.2rem 0.6rem 0.2rem 0.6rem;">
                <b-badge
                  pill
                  :variant="(skin === 'dark')? `light-danger` : `danger`"
                >
                  {{ (data.item.point !== '')? data.item.point : '' }}
                </b-badge>
              </b-list-group-item>
            </b-list-group>
          </div>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">

          <div class="text-nowrap">
            <feather-icon
              :id="`refund-row-${data.item.id}-edit-icon`"
              icon="EditIcon"
              class="cursor-pointer mr-1"
              size="16"
              @click="editRefundForm(data)"
            />
            <b-tooltip
              :title="`${$t('Edit')} ${$t('Refund')}`"
              :target="`refund-row-${data.item.id}-edit-icon`"
              :delay="{ show: 1000, hide: 50 }"
              placement="left"
            />

            <feather-icon
              :id="`refund-row-${data.item.id}-delete-icon`"
              icon="TrashIcon"
              variant="light-success"
              size="16"
              class="cursor-pointer mr-1"
              @click="deleteRefundForm(data)"
            />
            <b-tooltip
              :title="`${$t('Delete')} ${$t('Refund')}`"
              :target="`refund-row-${data.item.id}-delete-icon`"
              :delay="{ show: 1000, hide: 50 }"
              placement="right"
            />

          </div>
        </template>

      </b-table>
      <div class="mx-2 mb-2">
        <b-row>

          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted">
              {{ $t('Showing {from} to {to} of {of} entries',
                    {from: dataMeta.from, to: dataMeta.to, of: dataMeta.of })
              }}
            </span>
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >

            <b-pagination
              v-model="currentPage"
              :total-rows="totalRefunds"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon
                  icon="ChevronLeftIcon"
                  size="18"
                />
              </template>
              <template #next-text>
                <feather-icon
                  icon="ChevronRightIcon"
                  size="18"
                />
              </template>
            </b-pagination>

          </b-col>

        </b-row>
      </div>
    </b-card>

    <refund-edit-form
      v-model="shallShowRefundEditFormModal"
      :refund-data="refundData"
      @refund-edit-form-update="updateRefundEditForm"
      @discard-refund-edit-form="discardRefundEditForm"
    />

  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BTable,
  BBadge, BPagination, BTooltip, BFormCheckbox, BListGroup, BListGroupItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  ref, onUnmounted,
} from '@vue/composition-api'
import store from '@/store'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import RefundListFilters from './RefundListFilters.vue'
import useRefundList from './useRefundList'
import refundStoreModule from '../refundStoreModule'
import RefundEditForm from './RefundEditForm.vue'

export default {
  components: {
    RefundListFilters,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BTable,
    BBadge,
    BPagination,
    BTooltip,
    BFormCheckbox,
    BListGroup,
    BListGroupItem,

    vSelect,
    FeatherIcon,
    RefundEditForm,
  },
  data() {
    return {
      defaultData: {},
      dialog: false,
      skin: store.state.appConfig.layout.skin,
      selected: [],
      checkSelected: false,
    }
  },
  mounted() {
    this.getDefaultRefund()
  },
  methods: {
    getDefaultRefund() {
      store
        .dispatch('refund-store/getDefaultRefund', { language: this.$i18n.locale })
        .then(response => {
          const okMsg = this.$i18n.t('Process on successfully')
          this.$toast({
            component: ToastificationContent,
            props: {
              title: okMsg,
              icon: 'CheckCircleIcon',
              variant: 'success',
            },
          },
          {
            position: 'bottom-right',
          })
          this.defaultData = response.data
          this.brandOptions = this.defaultData.brand_options
          this.merchantOptions = this.defaultData.merchant_options
          this.valueOptions = this.defaultData.value_options
          this.statusOptions = this.defaultData.status_options
        })
        .catch(error => {
          const { response, message } = error
          if (response) {
            const errorMsg = this.$i18n.t(response?.data.error)
            this.$toast({
              component: ToastificationContent,
              props: {
                title: errorMsg,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          } else if (message) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: error?.message,
                icon: 'AlertTriangleIcon',
                variant: 'danger',
              },
            },
            {
              position: 'bottom-right',
            })
          }
        })
    },
    onRowSelected(items) {
      this.selected = items
      if (this.selected.length > 0) {
        this.checkSelected = true
      } else {
        this.checkSelected = false
      }
    },
    changeSelect() {
      if (this.checkSelected === true) {
        this.$refs.refRefundListTable.selectAllRows()
      } else {
        this.$refs.refRefundListTable.clearSelected()
      }
    },
    editRefundForm(data) {
      this.refundData = data.item
      this.shallShowRefundEditFormModal = true
    },
    deleteRefundForm(data) {
      this.$swal({
        title: this.$i18n.t('Are you sure?'),
        text: this.$i18n.t("You won't be able to revert this"),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: this.$i18n.t('Yes, delete it'),
        cancelButtonText: this.$i18n.t('Cancel'),
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value === true) {
          store.dispatch('refund-store/deleteRefund', data.item.id)
            .then(() => {
              const okMsg = this.$i18n.t('Process on successfully')
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: okMsg,
                  icon: 'CheckCircleIcon',
                  variant: 'success',
                },
              },
              {
                position: 'bottom-right',
              })
              this.$swal({
                icon: 'success',
                title: this.$i18n.t('Deleted'),
                text: this.$i18n.t('Your data has been deleted'),
                timer: 3000,
                customClass: {
                  confirmButton: 'btn btn-success',
                },
              })
              this.refetchData()
            })
            .catch(error => {
              const { response, message } = error
              if (response) {
                let errorMsg = this.$i18n.t(response?.data.error)
                if (errorMsg === '') {
                  errorMsg = this.$i18n.t(response?.data.message)
                }
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: errorMsg,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                },
                {
                  position: 'bottom-right',
                })
              } else if (message) {
                this.$toast({
                  component: ToastificationContent,
                  props: {
                    title: error?.message,
                    icon: 'AlertTriangleIcon',
                    variant: 'danger',
                  },
                },
                {
                  position: 'bottom-right',
                })
              }
            })
        }
      })
    },
    updateRefundEditForm() {
      this.resetRefundEditData()
      this.refetchData()
    },
    discardRefundEditForm() {
      this.resetRefundEditData()
      this.refetchData()
    },
  },
  setup() {
    const shallShowRefundEditFormModal = ref(false)

    const STORE_MODULE_NAME = 'refund-store'

    // Register module
    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, refundStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const brandOptions = []

    const merchantOptions = []

    const {
      getRefunds,
      tableColumns,
      perPage,
      currentPage,
      totalRefunds,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refRefundListTable,
      refetchData,

      // Extra Filters
      brandFilter,
      merchantFilter,
      codeFilter,
      refidFilter,
    } = useRefundList()

    const filterRefundList = JSON.parse(localStorage.getItem('filterRefundList'))
    if (filterRefundList !== null) {
      searchQuery.value = filterRefundList.searchQuery
      perPage.value = filterRefundList.perPage
      currentPage.value = filterRefundList.page
      sortBy.value = filterRefundList.sortBy
      isSortDirDesc.value = filterRefundList.sortDesc
      brandFilter.value = filterRefundList.brand
      merchantFilter.value = filterRefundList.merchant
      codeFilter.value = filterRefundList.code
      refidFilter.value = filterRefundList.refid
    }
    const blankRefund = {}
    const refundData = ref(JSON.parse(JSON.stringify(blankRefund)))
    const resetRefundEditData = () => {
      refundData.value = JSON.parse(JSON.stringify(blankRefund))
    }

    return {
      shallShowRefundEditFormModal,
      getRefunds,
      tableColumns,
      perPage,
      currentPage,
      totalRefunds,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refRefundListTable,
      refetchData,

      brandOptions,
      merchantOptions,

      // Extra Filters
      brandFilter,
      merchantFilter,
      codeFilter,
      refidFilter,

      refundData,
      resetRefundEditData,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}

.refund-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import "~@core/scss/base/e-coupon/refund.scss";
</style>

<style lang="scss">
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.dark-layout {
  div{
      .b-overlay {
        .bg-light {
          background-color: $theme-dark-body-bg !important;
        }
      }
      .swal2-popup {
        background-color: $theme-dark-body-bg !important;
      }
  }
  .table.b-table > tbody .b-table-row-selected.table-active td {
    background-color: $theme-dark-table-active-bg;
  }
  .table.b-table.table-hover > tbody > tr.table-active:hover td,
  .table.b-table.table-hover > tbody > tr.table-active:hover th {
    background-image: linear-gradient($theme-dark-table-active-hover-bg, $theme-dark-table-active-hover-bg);
  }
}
.b-table-selectable{
  .feather{
    font-size: 1.3rem;
  }
}
</style>
